import { useState } from 'react';
import { useCadencia } from '@/api/queries/empresa';
import { useCadenciaAction, type CadenciaAction } from '@/api/mutations/cadencia';
import { Skeleton } from '@/components/ui/skeleton';
import { Play, Pause, RotateCcw, ChevronRight, MessageSquare, CalendarCheck, Skull, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { toast } from 'sonner';

const STEP_LABEL: Record<string, string> = {
  queued: 'enfileirado',
  d1_sent: 'msg 1 enviada',
  d4_sent: 'msg 2 enviada (D+4)',
  d10_sent: 'msg 3 enviada (D+10)',
  dead: 'morto',
  replied: 'respondeu',
  meeting: 'reunião marcada',
};

const STEP_STYLE: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  d1_sent: 'bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-200',
  d4_sent: 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100',
  d10_sent: 'bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-100',
  replied: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100',
  meeting: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100',
  dead: 'bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200',
};

function ActionBtn({
  icon: Icon,
  label,
  onClick,
  loading,
  variant = 'default',
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  label: string;
  onClick: () => void;
  loading?: boolean;
  variant?: 'default' | 'primary' | 'destructive';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50',
        variant === 'primary' && 'bg-primary text-primary-foreground hover:bg-primary/90',
        variant === 'destructive' && 'border border-destructive/40 text-destructive hover:bg-destructive/10',
        variant === 'default' && 'border border-border/70 bg-card hover:bg-muted',
      )}
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" strokeWidth={2.25} />}
      {label}
    </button>
  );
}

export function TabCadencia({ empresaId }: { empresaId: string }) {
  const { data, isLoading } = useCadencia(empresaId);
  const action = useCadenciaAction();
  const [pendingAction, setPendingAction] = useState<CadenciaAction | null>(null);

  async function exec(a: CadenciaAction, params?: Record<string, string>) {
    setPendingAction(a);
    try {
      await action.mutateAsync({ empresa_id: empresaId, action: a, params });
      const labels: Record<CadenciaAction, string> = {
        start: 'cadência iniciada',
        pause: 'pausada',
        resume: 'retomada',
        advance: 'avançou 1 passo',
        mark_replied: 'marcada como respondeu',
        mark_meeting: 'marcada como reunião',
        mark_dead: 'marcada como morta',
      };
      toast.success(labels[a]);
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    } finally {
      setPendingAction(null);
    }
  }

  if (isLoading) return <Skeleton className="h-24 mt-4" />;

  // Cadência ainda não foi criada
  if (!data) {
    return (
      <div className="mt-4 rounded-md border border-dashed border-border/70 bg-muted/30 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Sem cadência ativa.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Iniciar uma cadência agenda automaticamente os toques de follow-up.
        </p>
        <div className="mt-3">
          <ActionBtn
            icon={Play}
            label="iniciar cadência"
            variant="primary"
            onClick={() => exec('start', { template: 'padrao_d1_d4_d10' })}
            loading={pendingAction === 'start'}
          />
        </div>
      </div>
    );
  }

  const isPaused = Boolean(data.pause_motivo);
  const isFinal = ['dead', 'replied', 'meeting'].includes(data.cadence_step);

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-border/70 bg-card p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium', STEP_STYLE[data.cadence_step])}>
                {STEP_LABEL[data.cadence_step] ?? data.cadence_step}
              </span>
              {isPaused && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  <Pause className="h-2.5 w-2.5" /> pausada · {data.pause_motivo}
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              template <span className="mono">{data.cadencia_template}</span>
            </p>
          </div>
        </div>

        <dl className="grid gap-x-6 gap-y-1 text-[12px] sm:grid-cols-2">
          <dt className="text-muted-foreground">próximo toque</dt>
          <dd className="mono tabular">
            {data.next_touch_at
              ? new Date(data.next_touch_at).toLocaleString('pt-BR', {
                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                })
              : '—'}
          </dd>
          <dt className="text-muted-foreground">último toque</dt>
          <dd className="mono tabular">
            {data.last_touch_at
              ? new Date(data.last_touch_at).toLocaleString('pt-BR', {
                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                })
              : '—'}
          </dd>
        </dl>
      </div>

      {!isFinal && (
        <div className="flex flex-wrap gap-2">
          {isPaused ? (
            <ActionBtn
              icon={Play}
              label="retomar"
              variant="primary"
              onClick={() => exec('resume')}
              loading={pendingAction === 'resume'}
            />
          ) : (
            <>
              <ActionBtn
                icon={ChevronRight}
                label="avançar passo"
                variant="primary"
                onClick={() => exec('advance')}
                loading={pendingAction === 'advance'}
              />
              <ActionBtn
                icon={Pause}
                label="pausar"
                onClick={() => exec('pause', { motivo: 'manual' })}
                loading={pendingAction === 'pause'}
              />
            </>
          )}
          <div className="ml-auto flex gap-2">
            <ActionBtn
              icon={MessageSquare}
              label="respondeu"
              onClick={() => exec('mark_replied')}
              loading={pendingAction === 'mark_replied'}
            />
            <ActionBtn
              icon={CalendarCheck}
              label="reunião"
              onClick={() => exec('mark_meeting')}
              loading={pendingAction === 'mark_meeting'}
            />
            <ActionBtn
              icon={Skull}
              label="morto"
              variant="destructive"
              onClick={() => exec('mark_dead')}
              loading={pendingAction === 'mark_dead'}
            />
          </div>
        </div>
      )}

      {isFinal && (
        <div className="flex justify-end">
          <ActionBtn
            icon={RotateCcw}
            label="reiniciar cadência"
            onClick={() => exec('start', { template: data.cadencia_template })}
            loading={pendingAction === 'start'}
          />
        </div>
      )}
    </div>
  );
}
